<script setup>
import { ref, computed, watch } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

// Default sidebar configuration with all options enabled
const DEFAULT_SIDEBAR_CONFIG = {
  show_all_conversations: true,
  show_inbox: true,
  show_conversation_groups: true,
  show_folders: true,
  show_teams: true,
  show_channels: true,
  show_labels: true,
};

const currentSettings = computed(() => ({
  ...DEFAULT_SIDEBAR_CONFIG,
  ...(uiSettings.value.sidebar_config || {}),
}));

const sidebarConfig = ref({ ...currentSettings.value });

watch(
  currentSettings,
  newSettings => {
    sidebarConfig.value = { ...newSettings };
  },
  { immediate: true }
);

const sidebarOptions = computed(() => [
  {
    key: 'show_all_conversations',
    label: t(
      'PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.OPTIONS.ALL_CONVERSATIONS'
    ),
    description: t(
      'PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.OPTIONS.ALL_CONVERSATIONS_DESC'
    ),
  },
  {
    key: 'show_inbox',
    label: t('PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.OPTIONS.INBOX'),
    description: t(
      'PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.OPTIONS.INBOX_DESC'
    ),
  },
  {
    key: 'show_conversation_groups',
    label: t(
      'PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.OPTIONS.CONVERSATION_GROUPS'
    ),
    description: t(
      'PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.OPTIONS.CONVERSATION_GROUPS_DESC'
    ),
  },
  {
    key: 'show_folders',
    label: t('PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.OPTIONS.FOLDERS'),
    description: t(
      'PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.OPTIONS.FOLDERS_DESC'
    ),
  },
  {
    key: 'show_teams',
    label: t('PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.OPTIONS.TEAMS'),
    description: t(
      'PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.OPTIONS.TEAMS_DESC'
    ),
  },
  {
    key: 'show_channels',
    label: t('PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.OPTIONS.CHANNELS'),
    description: t(
      'PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.OPTIONS.CHANNELS_DESC'
    ),
  },
  {
    key: 'show_labels',
    label: t('PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.OPTIONS.LABELS'),
    description: t(
      'PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.OPTIONS.LABELS_DESC'
    ),
  },
]);

const isUpdating = ref(false);

const updateSidebarConfiguration = async () => {
  isUpdating.value = true;
  try {
    updateUISettings({
      sidebar_config: sidebarConfig.value,
    });
    useAlert(t('PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.UPDATE_ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

const hasChanges = computed(() => {
  return (
    JSON.stringify(sidebarConfig.value) !==
    JSON.stringify(currentSettings.value)
  );
});

const toggleOption = key => {
  sidebarConfig.value[key] = !sidebarConfig.value[key];
};
</script>

<template>
  <div class="space-y-4">
    <div class="space-y-3">
      <div
        v-for="option in sidebarOptions"
        :key="option.key"
        class="p-4 rounded-lg cursor-pointer transition-all duration-200 hover:bg-ash-50 dark:hover:bg-ash-500"
        :class="[
          sidebarConfig[option.key]
            ? 'border-2 border-primary-600 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20'
            : 'border border-ash-300 dark:border-ash-600',
        ]"
        @click="toggleOption(option.key)"
      >
        <div class="flex items-start justify-between">
          <div class="flex-1 min-w-0">
            <h4 class="text-sm font-medium text-ash-900 dark:text-ash-1000">
              {{ option.label }}
            </h4>
            <p class="mt-1 text-sm text-ash-600 dark:text-ash-800">
              {{ option.description }}
            </p>
          </div>
          <div
            v-if="sidebarConfig[option.key]"
            class="ml-4 flex-shrink-0 text-primary-600 dark:text-white"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <div class="pt-4">
      <Button
        :disabled="!hasChanges || isUpdating"
        :loading="isUpdating"
        size="sm"
        @click="updateSidebarConfiguration"
      >
        {{ t('PROFILE_SETTINGS.FORM.SIDEBAR_CONFIGURATION.SAVE_BUTTON') }}
      </Button>
    </div>
  </div>
</template>
